import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { LayerService } from '@/core/services/open-api/layer.service';
import { LoadingService } from '@/core/services/loading.service';
import { Layer } from '@/core/models/layer';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@/core/services/open-api/user.service';
import { CurrentUser } from '@/core/models/user';
import { Column, LogoOptionType, PdfOrientationType, ReportSettings } from '@/core/models/report-settings.interface';
import { Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { LogoOption } from '@/core/models/logo-option.interface';
import { AssetUtilsService } from '@/core/services/utils/asset-utils.service';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { SelectOption } from '@/core/types/angular-primeng-api';
import { HttpErrorResponse } from '@angular/common/http';
import { Logo } from '@/core/models/logo-types';
import { LogoService } from '@/core/services/logo.service';
import { ToastService } from '@/core/services/toast.service';
import { ReportSettingsService } from '@/core/services/report-settings/report-settings.service';
import { getSelectOptionValueOrFirst } from '@/core/utils/angular-utils';
import { ReportSettingDefaults } from '@/core/services/report-settings/report-setting-defaults';
import { CloneUtils } from '@/core/utils/clone-utils';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { TimeFormat } from '@/core/models/time-format';

@Component({
    selector: 'app-report-customization',
    templateUrl: './report-customization.component.html',
    styleUrls: ['./report-customization.component.scss'],
    standalone: false,
})
export class ReportCustomizationComponent implements OnInit, OnDestroy {
    @Input() showDialog: boolean = false;
    @Input() uploadedCustomLogoUrl: SafeUrl | null = null;
    @Input() userSelectedLogoUrl: SafeUrl | null = null;
    @Output() userLogoSelectionUrlChange = new EventEmitter<SafeUrl | null>();
    @Output() showDialogChange = new EventEmitter<boolean>();
    layers: Layer[] = [];
    licenseStatePro = false;
    isAdmin = false;
    isAdminOrManager = false;
    private allSubscriptions = new Subscription();
    private initMgr: InitializationManager;
    // report settings form values:
    dateFormats: SelectOption[] = [];
    selectedDateFormat?: string;
    timeFormats: SelectOption<TimeFormat>[] = [];
    selectedTimeFormat?: TimeFormat;
    durationFormats: SelectOption[] = [];
    selectedDurationFormat?: string;
    reportLogoOptions: LogoOption[] = ReportSettingDefaults.getLogoOptions();
    reportLogoOption?: LogoOption;
    defaultLogoBase64: Logo = null;
    pdfReportHeading: string = ReportSettingDefaults.defaultPdfHeading;
    pdfOrientationOptions?: SelectOption[];
    pdfOrientation?: PdfOrientationType;
    tableColumns: Column[] = [];

    constructor(
        private readonly translate: TranslateService,
        private toastService: ToastService,
        private userService: UserService,
        private readonly loadingService: LoadingService,
        private readonly layerService: LayerService,
        private readonly router: Router,
        private readonly logoService: LogoService,
        private readonly reportSettingsService: ReportSettingsService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    // Allows the user to click the edit icon within the corganization logo dropdown
    public onButtonClick(event: MouseEvent): void {
        event.stopPropagation(); // This prevents the click from selecting the dropdown item
        void this.router.navigate(['/settings']);
    }

    public isLogoUploaded(): boolean {
        return this.uploadedCustomLogoUrl !== null;
    }

    // convert the reportLogoOption to an object
    private convertReportLogoOptionToObj = (reportLogoOption: string | null): LogoOption | undefined => {
        if (!reportLogoOption) return;
        return this.reportLogoOptions.find((l) => l.code === reportLogoOption);
    };

    public onReportHeadingBlur(): void {
        // empty strings are not allowed
        if (!this.pdfReportHeading.trim()) {
            this.pdfReportHeading = ReportSettingDefaults.defaultPdfHeading;
        }
    }

    public ngOnInit(): void {
        // add router subscription
        this.allSubscriptions.add(
            this.router.events.subscribe(() => {
                this.showDialogChange.emit(false);
            }),
        );

        this.initMgr
            .loadingAnimation(false)
            .addDependency(this.logoService.getDefaultLogoObservable(), {
                name: 'defaultLogo$',
                callback: (logo: Logo) => (this.defaultLogoBase64 = logo),
            })
            // make sure that necessary translations are loaded, they are used inside
            // ReportSettingDefaults.getTranslatedPdfOrientationOptions() ...
            .addDependency(this.translate.stream(['pdf_orientation_portrait', 'pdf_orientation_landscape']), {
                name: 'translations$',
            })
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (currentUser: CurrentUser) => {
                    this.licenseStatePro = currentUser.isPro();
                    this.isAdminOrManager = currentUser.isAdminOrManager();
                    this.isAdmin = currentUser.isAdmin();

                    const userId = currentUser.id;
                    const logoInLocalStorage = LocalStorageService.getUserUploadedLogo(userId);
                    if (logoInLocalStorage) {
                        this.uploadedCustomLogoUrl = logoInLocalStorage;
                    }
                },
            })
            .addDependency(this.reportSettingsService.currentReportSettings$, { name: 'reportSettings$' })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers: Layer[]) => (this.layers = layers),
            })
            .onInit(async ([_defaultLogo, _trans, _currentUser, reportSettings, _layers]) => {
                this.initComponentForm(reportSettings);
            })
            .initialize();
    }

    private initComponentForm(settings: ReportSettings): void {
        this.pdfOrientationOptions = ReportSettingDefaults.getTranslatedPdfOrientationOptions(this.translate);

        // default list of date formats
        this.dateFormats = ReportSettingDefaults.getDateFormatOptions();

        // default list of time formats
        this.timeFormats = ReportSettingDefaults.getTranslatedTimeFormatOptions(this.translate);

        // default list of duration formats
        this.durationFormats = ReportSettingDefaults.getDurationFormatOptions();

        this.tableColumns = CloneUtils.cloneData(settings.tableColumns);

        // sets the dropdowns to the current user's settings
        this.selectedDateFormat = getSelectOptionValueOrFirst(this.dateFormats, settings.dateFormat);
        this.selectedTimeFormat = getSelectOptionValueOrFirst<TimeFormat>(this.timeFormats, settings.timeFormat);
        this.selectedDurationFormat = getSelectOptionValueOrFirst(this.durationFormats, settings.durationFormat);

        this.reportLogoOption = this.convertReportLogoOptionToObj(settings.logoOption) ?? undefined;

        this.pdfReportHeading = settings.heading;

        this.pdfOrientation = getSelectOptionValueOrFirst(
            this.pdfOrientationOptions,
            settings.pdfOrientation,
        ) as PdfOrientationType;

        // set the logo based on the logoOption selected in the settings
        this.emitLogoUrlChange(settings.logoOption);
    }

    private emitLogoUrlChange(logoOption: LogoOptionType | null | undefined): void {
        if (logoOption != null) {
            if (logoOption === this.reportLogoOptions[0].code) {
                this.userLogoSelectionUrlChange.emit(AssetUtilsService.DEFAULT_LOGO_PATH);
            } else if (logoOption === this.reportLogoOptions[1].code) {
                this.userLogoSelectionUrlChange.emit(this.uploadedCustomLogoUrl);
            } else {
                console.error('emitLogoUrlChange() - Invalid logo option:', logoOption);
            }
        }
    }

    ngOnDestroy(): void {
        this.allSubscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    public onHide(): void {
        this.showDialogChange.emit(false);
    }

    // Cancel button
    public onCancel(): void {
        this.onHide();
        this.resetSettingsToOriginal();
    }

    public getDialogHeader(): string {
        let reportCustomizationHeader: string = '';
        this.translate.get(['report_customization_header']).subscribe((translations: { [key: string]: string }) => {
            reportCustomizationHeader = translations['report_customization_header'];
        });
        return reportCustomizationHeader;
    }

    /**
     * Save the changes made by the user in the modal
     */
    public save(): void {
        this.loadingService.setLoading(true);
        this.reportSettingsService
            .updateReportSettings({
                dateFormat: this.selectedDateFormat,
                timeFormat: this.selectedTimeFormat,
                durationFormat: this.selectedDurationFormat,
                tableColumns: this.tableColumns,
                logoOption: this.reportLogoOption?.code ?? null,
                heading: this.pdfReportHeading,
                pdfOrientation: this.pdfOrientation,
            })
            .subscribe({
                next: (_currentUser: CurrentUser) => {
                    this.emitLogoUrlChange(this.reportLogoOption?.code);
                    this.toastService.toast('SAVE_SUCCESS');
                    this.loadingService.setLoading(false);
                    this.onHide();
                },
                error: (_error: HttpErrorResponse) => {
                    this.toastService.error('standard_error', 'profile_profilesavingerror');
                    this.loadingService.setLoading(false);
                },
            });
    }

    private resetSettingsToOriginal(): void {
        this.initComponentForm(this.reportSettingsService.getNotModified()!);
    }
}
